import React, { Fragment, useEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import styles from './Finances.module.scss';

import { routes } from 'enums';
import { LeftArrow, RightArrow, Search, Pencil, SaveIcon } from 'assets/icons';
import { useActions, useAppSelector, useDebounce } from 'hooks';
import { doctorThunk, notificationThunk, notificationsSelect, userSelect } from 'state';
import { CustomButton } from 'components/CustomButton';
import { NotificationModal } from 'components/NotificationModal';
import clsx from 'clsx';
import { userThunk } from 'state/thunks/userThunk/userThunk';
import { usersSelect } from 'state/selectors/userSelectors';
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import { doctorsSelect } from 'state/selectors/doctorSelectors';
import { stat } from 'fs/promises';
import { CustomDropdown } from 'ui-kit/CustomDropdown';
import { financeThunk } from 'state/thunks/financeThunk/financeThunk';
import { financesSelect } from 'state/selectors/financeSelectors';
import { EmptyStub } from 'components/EmptyStub';
import { SettlementModal } from 'components/SettlementModal';
import { IconButton } from 'components/IconButton';

const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const FinanceItem = ({
  user,
  columns,
  columnWidth,
  handleEditClick,
  isEditable,
  handleChangePaymentStatus,
}: any) => {
  console.log(user);
  const itemsObj: any = {
    'Provider name': (
      <div className={styles.customCol1} style={{ width: columnWidth + '%' }}>
        {user.user.doctor.name}
      </div>
    ),
    'Specialist Type': (
      <div className={styles.customCol1} style={{ width: columnWidth + '%' }}>
        {user.user.doctor.category}
      </div>
    ),
    Speciality: (
      <div className={styles.customCol1} style={{ width: columnWidth + '%' }}>
        {user.user.doctor.speciality ? user.user.doctor.speciality : '-'}
      </div>
    ),
    'For month': (
      <div className={styles.customCol1} style={{ width: columnWidth + '%' }}>
        {months[user.month]}
      </div>
    ),
    Amount: (
      <div className={styles.customCol1} style={{ width: columnWidth + '%' }}>
        {/* EGP, not dollars — matches billing_settings.currency and the
            commission screen. */}
        {Number(user.amount).toFixed(2)} EGP
      </div>
    ),
    'Due date': (
      <div className={styles.customCol1} style={{ width: columnWidth + '%' }}>
        {/* Was a hardcoded '-', so the column never showed anything. The API
            returns `dueDate` (stamped by the billing run when the month
            closes); it is genuinely null only while the invoice is still
            accruing, which is what the dash now means. */}
        {user.dueDate ? new Date(user.dueDate).toLocaleDateString('en-GB') : '-'}
      </div>
    ),
    'Payment status': !isEditable ? (
      <div
        className={styles.customCol1}
        style={{ width: columnWidth + '%', fontWeight: 'bold' }}
      >
        {user.paymentStatus}
      </div>
    ) : (
      <div
        className={styles.customCol1}
        style={{ width: columnWidth + '%', fontWeight: 'bold' }}
      >
        <FormControl sx={{ m: 1, minWidth: 70 }}>
          <Select
            data-testid="finance-status-select"
            value={user.paymentStatus}
            onChange={e => {
              // Opens the settlement dialog rather than writing the status.
              // This used to PATCH `{...user, paymentStatus}` -- the whole
              // finance row -- which recorded THAT an invoice was settled and
              // never HOW, so cash received and dues written off became the
              // same row and the list could not be reconciled against a bank
              // statement. Nothing is sent until the admin confirms.
              handleChangePaymentStatus(user.id, e.target.value as string, user);
            }}
            displayEmpty
            inputProps={{ 'aria-label': 'Without label' }}
            style={{ height: 40, padding: 0 }}
          >
            <MenuItem value={'Not Paid'}>Not Paid</MenuItem>
            <MenuItem value={'Paid'}>Paid</MenuItem>
          </Select>
        </FormControl>
      </div>
    ),
    Status: (
      <div
        className={styles.customCol1}
        id="Status"
        style={
          user.user.userStatus === 'Active'
            ? { color: 'green', fontWeight: 'bold' }
            : { color: 'red', fontWeight: 'bold' }
        }
      >
        {user.user.userStatus}
      </div>
    ),
  };

  return (
    <div className={styles.tableItem}>
      {columns.map((column: any) =>
        column.isSelect ? (
          // itemsObj holds bare elements, so the key has to go on a wrapper.
          <Fragment key={column.name}>{itemsObj[column.name]}</Fragment>
        ) : null,
      )}
      <button
        type="button"
        aria-label={isEditable ? 'Save row' : 'Edit row'}
        onClick={handleEditClick}
        className={styles.customCol1}
        style={{ width: columnWidth / 2 + '%', cursor: 'pointer' }}
        // The row's status dropdown does not exist until this is clicked, and
        // an icon offers a test nothing else to aim at. Without a hook the
        // settlement suite can only guess at nth-of-type selectors, which break
        // the next time a column moves.
        data-testid="finance-row-edit"
      >
        {isEditable ? <SaveIcon /> : <Pencil />}
      </button>
    </div>
  );
};

const defaultColumns = [
  {
    name: 'Provider name',
    isSelect: true,
  },
  {
    name: 'Specialist Type',
    isSelect: true,
  },
  {
    name: 'Speciality',
    isSelect: true,
  },
  {
    name: 'For month',
    isSelect: true,
  },
  {
    name: 'Amount',
    isSelect: true,
  },
  {
    name: 'Due date',
    isSelect: true,
  },
  {
    name: 'Payment status',
    isSelect: true,
  },
  {
    name: 'Status',
    isSelect: true,
  },
];

export const Finances = () => {
  const navigate = useNavigate();
  const [isNotificationModal, setNotificationModal] = useState(false);
  const { getUsers } = useActions(userThunk);
  const { getDoctors } = useActions(doctorThunk);
  const { getFinances, editFinance } = useActions(financeThunk);
  const users: any = useAppSelector(usersSelect);
  const doctors: any = useAppSelector(doctorsSelect);
  const finances: any = useAppSelector(financesSelect);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [bodyMessage, setBodyMessage] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [statusValue, setStatusValue] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [columns, setColumns] = useState(defaultColumns);
  const [columnWidth, setColumnWidth] = useState(100);
  const [selectedEditableItem, setSelectedEditableItem]: any = useState([]);

  const handleColumnChange = (name: any) => {
    console.log(name);
    setColumns(
      columns.map(el => {
        if (el.name === name) {
          return {
            ...el,
            isSelect: !el.isSelect,
          };
        } else {
          return el;
        }
      }),
    );
  };

  // useEffect(() => {
  //   getColumnWidth();
  // }, [columns]);

  useEffect(() => {
    const columnCount = getColumnWidth();
    setColumnWidth(columnCount);
  }, [columns]);

  const getColumnWidth = () => {
    const columnCount = columns.reduce((accumulator: any, el: any) => {
      if (el.isSelect) {
        return accumulator + 1;
      }
      return accumulator;
    }, 0);

    console.log(columnCount);

    return 100 / columnCount;
  };

  const debouncedSearchValue = useDebounce(searchValue, 500);

  const convertStatusValue = () => {
    switch (statusValue) {
      case 'active':
        return true;
        break;
      case 'blocked':
        return false;
        break;
      case '':
        return '';
    }
  };

  useEffect(() => {
    if (debouncedSearchValue) {
      setIsSearching(true);
      getFinances({
        q: debouncedSearchValue,
        take: itemPerPage,
        page: '',
        category: selectedCategory,
        status: convertStatusValue(),
        year: selectedYear,
      });
    } else {
      getFinances({
        q: searchValue,
        take: itemPerPage,
        page: '',
        category: selectedCategory,
        status: convertStatusValue(),
        year: selectedYear,
      });
    }
  }, [debouncedSearchValue]);

  const itemPerPage = 10;

  useEffect(() => {
    getUsers({ q: '', take: itemPerPage, page: '' });
    getFinances({
      q: '',
      take: itemPerPage,
      page: '',
      category: selectedCategory,
      status: convertStatusValue(),
      year: selectedYear,
    });
  }, []);

  useEffect(() => {
    console.log('----');
    console.log(finances);
  }, [finances]);

  useEffect(() => {
    getFinances({
      q: debouncedSearchValue,
      take: itemPerPage,
      page: '',
      category: selectedCategory,
      status: convertStatusValue(),
      year: selectedYear,
    });
  }, [selectedCategory]);

  const handleLeftArrowClick = () => {
    if (finances?.page > 1) {
      getFinances({
        q: debouncedSearchValue,
        take: itemPerPage,
        page: finances?.page - 1,
        category: selectedCategory,
        status: convertStatusValue(),
        year: selectedYear,
      });
    }
  };

  const handleRightArrowClick = () => {
    if (finances?.page * itemPerPage < finances.count) {
      getFinances({
        q: debouncedSearchValue,
        take: itemPerPage,
        page: finances?.page + 1,
        category: selectedCategory,
        status: convertStatusValue(),
        year: selectedYear,
      });
    }
  };

  useEffect(() => {
    if (statusValue === '') {
      getFinances({
        q: debouncedSearchValue,
        take: itemPerPage,
        page: '',
        status: '',
        category: selectedCategory,
        year: selectedYear,
      });
    }

    if (statusValue === 'active') {
      getFinances({
        q: debouncedSearchValue,
        take: itemPerPage,
        page: '',
        status: convertStatusValue(),
        category: selectedCategory,
        year: selectedYear,
      });
    }
    if (statusValue === 'blocked') {
      getFinances({
        q: debouncedSearchValue,
        take: itemPerPage,
        page: '',
        status: convertStatusValue(),
        category: selectedCategory,
        year: selectedYear,
      });
    }
  }, [statusValue]);

  const handleEditClick = (index: any) => {
    if (selectedEditableItem.includes(index)) {
      setSelectedEditableItem(selectedEditableItem.filter((el: any) => el !== index));
    } else {
      setSelectedEditableItem([...selectedEditableItem, index]);
    }
  };

  /**
   * Holds the invoice an admin is part-way through settling or reopening.
   *
   * Nothing is sent while this is set: cancelling simply clears it, and because
   * the Select reads its value from the store rather than local state, the
   * dropdown falls back to what the invoice actually says on its own.
   */
  const [pendingSettlement, setPendingSettlement] = useState<any>(null);

  const handleChangePaymentStatus = (id: any, paymentStatus: string, row: any) => {
    setPendingSettlement({ id, paymentStatus, row });
  };

  const confirmSettlement = ({ settlementMethod, note }: any) => {
    const { id, paymentStatus } = pendingSettlement;

    // Only the fields the endpoint accepts. The server whitelists the body
    // anyway -- it has to, because this page used to send the entire row -- but
    // sending what is meant is how the next reader learns what is meant.
    editFinance({
      id,
      data: {
        paymentStatus,
        ...(settlementMethod ? { settlementMethod } : {}),
        ...(note ? { note } : {}),
      },
    });

    setPendingSettlement(null);
  };

  // Was hard-coded to '2025'. Every finance query is filtered by this year, so
  // once the calendar moved on the page listed nothing — the same defect the
  // dashboard had.
  const [selectedYear, setSelectedYear] = useState(String(new Date().getFullYear()));

  const availableYears: any = [
    '2023',
    '2024',
    '2025',
    '2026',
    '2027',
    '2028',
    '2029',
    '2030',
    '2031',
    '2032',
    '2033',
    '2034',
    '2035',
    '2036',
    '2037',
    '2038',
    '2039',
    '2040',
    '2041',
    '2042',
    '2043',
    '2044',
    '2045',
    '2046',
    '2047',
    '2048',
    '2049',
    '2050',
    '2051',
    '2052',
    '2053',
    '2054',
    '2055',
  ];

  useEffect(() => {
    getFinances({
      q: debouncedSearchValue,
      take: itemPerPage,
      page: '',
      category: selectedCategory,
      status: convertStatusValue(),
      year: selectedYear,
    });
  }, [selectedYear]);

  return (
    <div className={styles.container}>
      {pendingSettlement && (
        <SettlementModal
          intent={pendingSettlement.paymentStatus}
          providerName={pendingSettlement.row?.user?.doctor?.name}
          amount={pendingSettlement.row?.amount}
          onConfirm={confirmSettlement}
          onCancel={() => setPendingSettlement(null)}
        />
      )}
      <div className={styles.header}>
        <div className={styles.flex}>
          <div className={styles.title}>Finances</div>
          <div className={styles.pagination}>
            {finances?.page * itemPerPage + 1 - itemPerPage}-
            {finances?.page * itemPerPage} from {finances?.count}
          </div>
          <IconButton
            label="Previous page"
            className={styles.arrowContainer}
            onClick={handleLeftArrowClick}
          >
            <LeftArrow />
          </IconButton>
          <IconButton
            label="Next page"
            className={styles.arrowContainer}
            onClick={handleRightArrowClick}
          >
            <RightArrow />
          </IconButton>
        </div>

        <div className={styles.flexFilters}>
          <div>
            <FormControl sx={{ m: 1, minWidth: 140 }}>
              <Select
                value={selectedYear}
                onChange={e => {
                  setSelectedYear(e.target.value);
                }}
                displayEmpty
                inputProps={{ 'aria-label': 'Without label' }}
                style={{ height: 40, padding: 0 }}
              >
                {availableYears.map((el: any) => (
                  <MenuItem key={el} value={el}>
                    Year: {el}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <div>
            <FormControl sx={{ m: 1, minWidth: 255 }}>
              <Select
                value={selectedCategory}
                onChange={e => {
                  setSelectedCategory(e.target.value);
                }}
                displayEmpty
                inputProps={{ 'aria-label': 'Without label' }}
                style={{ height: 40, padding: 0 }}
              >
                <MenuItem value={''}>Type: All</MenuItem>
                <MenuItem value={'GP/Emergency doctor'}>
                  Type: GP/Emergency doctor
                </MenuItem>
                <MenuItem value={'Nurse'}>Type: Nurse</MenuItem>
                <MenuItem value={'Specialist doctor'}>Type: Specialist doctor</MenuItem>
                <MenuItem value={'Consultant doctor'}>Type: Consultant doctor</MenuItem>
                <MenuItem value={'Physio therapist'}>Type: Physio therapist</MenuItem>
              </Select>
            </FormControl>
          </div>
          <div>
            <FormControl sx={{ m: 1, minWidth: 120 }}>
              <Select
                value={statusValue}
                onChange={e => {
                  setStatusValue(e.target.value);
                }}
                displayEmpty
                inputProps={{ 'aria-label': 'Without label' }}
                style={{ height: 40, padding: 0 }}
              >
                <MenuItem value={''}>Status: All</MenuItem>
                <MenuItem value={'active'}>Status: Active</MenuItem>
                <MenuItem value={'blocked'}>Status: Blocked</MenuItem>
              </Select>
            </FormControl>
          </div>

          <CustomDropdown columns={columns} handleColumnChange={handleColumnChange} />

          <div className={styles.inputContainerSearch}>
            <div className={styles.searchIconContainer}>
              <Search />
            </div>

            <input
              value={searchValue}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSearchValue(e.target.value)
              }
              placeholder="Search by ProviderName"
              className={styles.inputSearch}
            />
          </div>
        </div>
      </div>

      {isNotificationModal && (
        <NotificationModal setNotificationModal={setNotificationModal} />
      )}

      <div className={styles.table}>
        <div className={styles.tableHeader}>
          {columns.map(el =>
            el.isSelect ? (
              <div
                key={el.name}
                className={clsx([styles.customCol1, styles.bold])}
                style={{ width: columnWidth + '%' }}
              >
                {el.name}
              </div>
            ) : null,
          )}
          <div
            className={clsx([styles.customCol1, styles.bold])}
            style={{ width: columnWidth / 2 + '%' }}
          ></div>
        </div>

        {finances?.rows && finances?.rows[0] ? (
          finances?.rows?.map((user: any, index: any) => {
            return (
              <FinanceItem
                key={user.id ?? index}
                handleChangePaymentStatus={handleChangePaymentStatus}
                handleEditClick={() => handleEditClick(index)}
                isEditable={selectedEditableItem.includes(index)}
                user={user}
                columns={columns}
                columnWidth={columnWidth}
              />
            );
          })
        ) : (
          <EmptyStub />
        )}
      </div>
    </div>
  );
};
